import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";

const API = "https://api.fullagro.com";

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));
}

export default function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);

  const [fUsername, setFUsername] = useState("");
  const [fEmail, setFEmail] = useState("");
  const [fPassword, setFPassword] = useState("");
  const [fRole, setFRole] = useState("cultivador");

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      setMe(decoded);
      if (decoded.user_type !== "admin") {
        setDenied(true);
        setLoading(false);
        return;
      }
    } catch (_) {
      navigate("/");
      return;
    }
    loadUsers();
    // eslint-disable-next-line
  }, []);

  async function api(path, opts = {}) {
    const token = localStorage.getItem("token");
    const res = await fetch(API + path, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
        ...(opts.headers || {}),
      },
    });
    const text = await res.text();
    let body;
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
    if (!res.ok) throw new Error(body.message || body.error || "HTTP " + res.status);
    return body;
  }

  async function loadUsers() {
    setLoading(true);
    try {
      const data = await api("/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error("Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q
      ? users
      : users.filter((u) => {
          const hay = [
            u.username,
            u.email,
            u.user_type,
            u.is_active ? "active" : "disabled",
            u.last_logged_in,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        });

    const sorted = [...filtered].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa < sb) return sortDir === "asc" ? -1 : 1;
      if (sa > sb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [users, search, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortHeader({ k, children }) {
    const active = sortKey === k;
    const arrow = !active ? "↕" : sortDir === "asc" ? "▲" : "▼";
    return (
      <th
        onClick={() => toggleSort(k)}
        style={{
          cursor: "pointer",
          userSelect: "none",
          color: active ? "#7ee787" : undefined,
        }}
        title="Click to sort"
      >
        {children} <span style={{ opacity: active ? 1 : 0.4, fontSize: 11 }}>{arrow}</span>
      </th>
    );
  }

  function openCreate() {
    setEditingId(null);
    setFUsername("");
    setFEmail("");
    setFPassword("");
    setFRole("cultivador");
    setModalOpen(true);
  }

  function openEdit(u) {
    setEditingId(u.id);
    setFUsername(u.username || "");
    setFEmail(u.email || "");
    setFPassword("");
    setFRole(u.user_type || "cultivador");
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
  }

  async function saveModal(e) {
    e.preventDefault();
    try {
      if (editingId == null) {
        if (!fUsername || !fEmail || !fPassword)
          return toast.error("All fields required");
        await api("/register", {
          method: "POST",
          body: JSON.stringify({
            username: fUsername,
            email: fEmail,
            password: fPassword,
            user_type: fRole,
          }),
        });
        toast.success("User created");
      } else {
        const body = { user_type: fRole };
        if (fPassword) body.password = fPassword;
        await api("/users/" + editingId, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        toast.success("User updated");
      }
      closeModal();
      loadUsers();
    } catch (e) {
      toast.error("Save failed: " + e.message);
    }
  }

  async function changeRole(id, user_type) {
    try {
      await api("/users/" + id, {
        method: "PUT",
        body: JSON.stringify({ user_type }),
      });
      toast.success("Role updated");
    } catch (e) {
      toast.error("Update failed: " + e.message);
      loadUsers();
    }
  }

  async function toggleActive(id) {
    try {
      await api("/users/" + id, { method: "DELETE" });
      toast.success("Status changed");
      loadUsers();
    } catch (e) {
      toast.error("Toggle failed: " + e.message);
    }
  }

  if (denied) {
    return (
      <main className="admin-route">
        <div className="admin-toolbar">
          <h2>Acceso denegado</h2>
          <div className="admin-actions">
            <button className="btn secondary" onClick={() => navigate("/")}>
              Volver al Store
            </button>
          </div>
        </div>
        <div className="admin-empty">
          Esta sección es solo para administradores.
        </div>
      </main>
    );
  }

  return (
    <main className="admin-route">
      <div className="admin-toolbar">
        <h2>
          User Management
          <span className="admin-toolbar-sub">Equipo</span>
        </h2>
        <div className="admin-actions">
          <button className="btn" onClick={openCreate}>
            + NEW USER
          </button>
          <button className="btn secondary" onClick={loadUsers}>
            REFRESH
          </button>
        </div>
      </div>

      <div className="admin-toolbar" style={{ marginTop: 8, gap: 12 }}>
        <input
          type="search"
          placeholder="Buscar por username, email o rol…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-search"
        />
        <span className="admin-toolbar-sub" style={{ marginLeft: "auto" }}>
          {visibleUsers.length} / {users.length} {users.length === 1 ? "user" : "users"}
        </span>
      </div>

      {loading ? (
        <div className="admin-empty">Cargando usuarios...</div>
      ) : users.length === 0 ? (
        <div className="admin-empty">No users found.</div>
      ) : visibleUsers.length === 0 ? (
        <div className="admin-empty">Sin coincidencias para "{search}".</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <SortHeader k="id">ID</SortHeader>
                <SortHeader k="username">Username</SortHeader>
                <SortHeader k="email">Email</SortHeader>
                <SortHeader k="user_type">Role</SortHeader>
                <SortHeader k="is_active">Status</SortHeader>
                <SortHeader k="last_logged_in">Last login</SortHeader>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => (
                <tr
                  key={u.id}
                  className={u.is_active ? "" : "inactive"}
                >
                  <td>{u.id}</td>
                  <td>
                    <strong>{escapeHtml(u.username)}</strong>
                  </td>
                  <td>{escapeHtml(u.email)}</td>
                  <td>
                    <select
                      value={u.user_type || "cultivador"}
                      onChange={(e) => changeRole(u.id, e.target.value)}
                    >
                      <option value="cultivador">cultivador</option>
                      <option value="consultor">consultor</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    <span
                      className={"badge " + (u.is_active ? "active" : "inactive")}
                    >
                      {u.is_active ? "active" : "disabled"}
                    </span>
                  </td>
                  <td>
                    {u.last_logged_in
                      ? new Date(u.last_logged_in).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <button className="btn secondary" onClick={() => openEdit(u)}>
                      EDIT
                    </button>
                    <button
                      className={"btn " + (u.is_active ? "danger" : "")}
                      onClick={() => toggleActive(u.id)}
                    >
                      {u.is_active ? "DISABLE" : "ENABLE"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="admin-modal-bg open" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId == null ? "New user" : "Edit user #" + editingId}</h3>
            <form onSubmit={saveModal}>
              <div className="admin-field">
                <label>Username</label>
                <input
                  type="text"
                  value={fUsername}
                  onChange={(e) => setFUsername(e.target.value)}
                  disabled={editingId != null}
                  required
                />
              </div>
              <div className="admin-field">
                <label>Email</label>
                <input
                  type="email"
                  value={fEmail}
                  onChange={(e) => setFEmail(e.target.value)}
                  disabled={editingId != null}
                  required
                />
              </div>
              <div className="admin-field">
                <label>
                  Password {editingId != null && "(leave empty to keep current)"}
                </label>
                <input
                  type="password"
                  value={fPassword}
                  onChange={(e) => setFPassword(e.target.value)}
                  required={editingId == null}
                />
              </div>
              <div className="admin-field">
                <label>Role</label>
                <select value={fRole} onChange={(e) => setFRole(e.target.value)}>
                  <option value="cultivador">cultivador</option>
                  <option value="consultor">consultor</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              <div className="admin-modal-row">
                <button type="button" className="btn secondary" onClick={closeModal}>
                  CANCEL
                </button>
                <button type="submit" className="btn">
                  SAVE
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        theme="dark"
      />
    </main>
  );
}